import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Autocomplete,
  TextField,
  FormControlLabel,
  Switch
} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { statusOptions, positionOptions } from '../../../utils/Enums';
import { getStaff } from '../../../services/UserService';

const FRANCHISE_STAFF_POSITION_IDS = [2, 4, 7];

const EditUserModal = ({ open, onClose, user, onSave, roles, businessUnits = [], isBusinessUnitScoped = false }) => {
  const { t } = useTranslation();
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const availablePositionOptions = isBusinessUnitScoped
    ? positionOptions.filter((position) => FRANCHISE_STAFF_POSITION_IDS.includes(position.id))
    : positionOptions;

  const getRecommendedScopeType = (roleId) => {
    const selectedRole = roles.find((role) => role.id === roleId);
    switch (selectedRole?.templateCode) {
      case 'ECOSYSTEM_ADMIN':
      case 'ECOSYSTEM_MANAGER':
        return 'ALL';
      case 'MAIN_MANAGER':
        return 'MAIN';
      case 'FRANCHISE_MANAGER':
        return 'BUSINESS_UNIT';
      case 'SALES':
      case 'PRODUCTION':
      case 'SALES_PRODUCTION':
        return 'ASSIGNED_LOCATIONS';
      default:
        return null;
    }
  };

  const getInitialScopeState = (currentUser) => {
    const scopes = currentUser?.scopes || [];
    if (!scopes.length) return { scopeType: 'ASSIGNED_LOCATIONS', businessUnitId: '' };
    const firstScope = scopes[0];
    if (firstScope.scopeType === 'BUSINESS_UNIT') {
      return { scopeType: 'BUSINESS_UNIT', businessUnitId: firstScope.businessUnitId || '' };
    }
    if (firstScope.scopeType === 'LOCATION') {
      return { scopeType: 'ASSIGNED_LOCATIONS', businessUnitId: '' };
    }
    return { scopeType: firstScope.scopeType, businessUnitId: '' };
  };

  const shouldForceWebDashboardAccess = (roleId) => {
    const selectedRole = roles.find((role) => role.id === roleId);
    return ['ECOSYSTEM_MANAGER', 'FRANCHISE_MANAGER'].includes(selectedRole?.templateCode);
  };

  const getInitialFormData = (currentUser) => ({
    ci: currentUser?.ci || '',
    firstName: currentUser?.firstName || '',
    lastName: currentUser?.lastName || '',
    phone: currentUser?.phone || '',
    roleId: currentUser?.role?.id || '',
    dateOfAdmission: currentUser?.dateOfAdmission ? dayjs(currentUser.dateOfAdmission) : dayjs(),
    status: currentUser?.status ?? '',
    address: currentUser?.address || '',
    birthdate: currentUser?.birthDate ? dayjs(currentUser.birthDate) : dayjs(),
    position: currentUser?.position ?? '',
    canAccessWebDashboard: shouldForceWebDashboardAccess(currentUser?.role?.id)
      ? true
      : (currentUser?.canAccessWebDashboard ?? false),
    manager: currentUser?.manager || null,
    ...getInitialScopeState(currentUser)
  });

  const [formData, setFormData] = useState(getInitialFormData(user));

  useEffect(() => {
    const initialFormData = getInitialFormData(user);
    setFormData({
      ...initialFormData,
      position: isBusinessUnitScoped && !FRANCHISE_STAFF_POSITION_IDS.includes(initialFormData.position)
        ? ''
        : initialFormData.position,
    });
    setErrors({});
  }, [user, isBusinessUnitScoped]);

  useEffect(() => {
    if (open) {
      getStaff().then(setStaff);
    }
  }, [open]);

  const buildScopes = () => {
    switch (formData.scopeType) {
      case 'ALL':
        return [{ scope_type: 'ALL' }];
      case 'MAIN':
        return [{ scope_type: 'MAIN' }];
      case 'BUSINESS_UNIT':
        return formData.businessUnitId
          ? [{ scope_type: 'BUSINESS_UNIT', business_unit_id: Number(formData.businessUnitId) }]
          : [];
      case 'ASSIGNED_LOCATIONS':
      default:
        return [{ scope_type: 'ASSIGNED_LOCATIONS' }];
    }
  };

  const validate = () => {
    const tempErrors = {};
    let isValid = true;

    if (!formData.ci.trim()) {
      tempErrors.ci = t('ci_required');
      isValid = false;
    }
    if (!formData.firstName.trim()) {
      tempErrors.firstName = t('firstName_required');
      isValid = false;
    }
    if (!formData.lastName.trim()) {
      tempErrors.lastName = t('lastName_required');
      isValid = false;
    }
    if (!formData.phone.trim()) {
      tempErrors.phone = t('phone_required');
      isValid = false;
    }
    if (!formData.roleId) {
      tempErrors.roleId = t('role_required');
      isValid = false;
    }
    if (!formData.dateOfAdmission) {
      tempErrors.dateOfAdmission = t('dateOfAdmission_required');
      isValid = false;
    }
    if (!formData.status && formData.status !== 0) {
      tempErrors.status = t('status_required');
      isValid = false;
    }
    if (!formData.address.trim()) {
      tempErrors.address = t('address_required');
      isValid = false;
    }
    if (!formData.birthdate) {
      tempErrors.birthdate = t('birthdate_required');
      isValid = false;
    }
    if (!formData.position && formData.position !== 0) {
      tempErrors.position = t('position_required');
      isValid = false;
    }
    if (formData.scopeType === 'BUSINESS_UNIT' && !formData.businessUnitId) {
      tempErrors.businessUnitId = 'Selecciona una franquicia';
      isValid = false;
    }

    setErrors(tempErrors);
    return isValid;
  };

  const handleSave = async () => {
    if (!validate()) return;

    setLoading(true);
    try {
      await onSave({
        ...formData,
        scopes: buildScopes()
      });
      onClose();
    } catch (error) {
      console.error('Error updating user:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (event) => {
    const { name, value } = event.target;

    if (name === 'roleId') {
      const recommendedScopeType = getRecommendedScopeType(value);

      setFormData((prev) => ({
        ...prev,
        roleId: value,
        canAccessWebDashboard: shouldForceWebDashboardAccess(value)
          ? true
          : prev.canAccessWebDashboard,
        ...(recommendedScopeType
          ? {
              scopeType: recommendedScopeType,
              businessUnitId: recommendedScopeType === 'BUSINESS_UNIT' ? prev.businessUnitId : ''
            }
          : {})
      }));
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleDateChange = (name, newValue) => {
    setFormData((prev) => ({
      ...prev,
      [name]: newValue
    }));
  };

  const handleWebDashboardAccessChange = (event) => {
    setFormData((prev) => ({
      ...prev,
      canAccessWebDashboard: event.target.checked
    }));
  };

  const handleManagerChange = (_, value) => {
    setFormData((prev) => ({
      ...prev,
      manager: value
    }));
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle><span className="roboto-regular">{t('edit_user')}</span></DialogTitle>
      <DialogContent>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} sx={{ mt: 0.5 }}>
            <TextField
              label={t('first_name')}
              fullWidth
              name="firstName"
              value={formData.firstName}
              onChange={handleChange}
              error={!!errors.firstName}
              helperText={errors.firstName}
            />
          </Grid>
          <Grid item xs={12} sm={6} sx={{ mt: 0.5 }}>
            <TextField
              label={t('last_name')}
              fullWidth
              name="lastName"
              value={formData.lastName}
              onChange={handleChange}
              error={!!errors.lastName}
              helperText={errors.lastName}
            />
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.canAccessWebDashboard}
                  onChange={handleWebDashboardAccessChange}
                />
              }
              label="Acceso al dashboard web"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label={t('ci')}
              fullWidth
              name="ci"
              value={formData.ci}
              onChange={handleChange}
              error={!!errors.ci}
              helperText={errors.ci}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label={t('phone')}
              fullWidth
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              error={!!errors.phone}
              helperText={errors.phone}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth error={!!errors.roleId}>
              <InputLabel>{t('role')}</InputLabel>
              <Select
                label={t('role')}
                name="roleId"
                value={formData.roleId}
                onChange={handleChange}
              >
                {roles.map((role) => (
                  <MenuItem key={role.id} value={role.id}>{role.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Autocomplete
              options={staff.filter((staffUser) => staffUser.email !== user.email)}
              getOptionLabel={(option) => `${option.firstName} ${option.lastName}`}
              isOptionEqualToValue={(option, value) => option.email === value?.email}
              value={formData.manager}
              onChange={handleManagerChange}
              clearOnEscape
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={`${t('manager')} (${t('optional')})`}
                  placeholder={t('select_manager')}
                  fullWidth
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Alcance</InputLabel>
              <Select
                label="Alcance"
                name="scopeType"
                value={formData.scopeType}
                onChange={handleChange}
              >
                <MenuItem value="ALL">Ecosistema</MenuItem>
                <MenuItem value="MAIN">Main</MenuItem>
                <MenuItem value="BUSINESS_UNIT">Franquicia</MenuItem>
                <MenuItem value="ASSIGNED_LOCATIONS">Sucursales asignadas</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          {formData.scopeType === 'BUSINESS_UNIT' && (
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth error={!!errors.businessUnitId}>
                <InputLabel>Franquicia</InputLabel>
                <Select
                  label="Franquicia"
                  name="businessUnitId"
                  value={formData.businessUnitId}
                  onChange={handleChange}
                >
                  {businessUnits.map((unit) => (
                    <MenuItem key={unit.id} value={unit.id}>{unit.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}
          <Grid item xs={12} sm={6}>
            <DatePicker
              label={t('date_of_admission')}
              value={formData.dateOfAdmission}
              onChange={(newValue) => handleDateChange('dateOfAdmission', newValue)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth
                  error={!!errors.dateOfAdmission}
                  helperText={errors.dateOfAdmission}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth error={!!errors.status}>
              <InputLabel>{t('status')}</InputLabel>
              <Select
                label={t('status')}
                name="status"
                value={formData.status}
                onChange={handleChange}
              >
                {statusOptions.map((status) => (
                  <MenuItem key={status.id} value={status.id}>{t(status.label)}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label={t('address')}
              fullWidth
              name="address"
              value={formData.address}
              onChange={handleChange}
              error={!!errors.address}
              helperText={errors.address}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <DatePicker
              label={t('birthdate')}
              value={formData.birthdate}
              onChange={(newValue) => handleDateChange('birthdate', newValue)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth
                  error={!!errors.birthdate}
                  helperText={errors.birthdate}
                />
              )}
            />
          </Grid>
          <Grid item xs={12}>
            <FormControl fullWidth error={!!errors.position}>
              <InputLabel>{t('position')}</InputLabel>
              <Select
                label={t('position')}
                name="position"
                value={formData.position}
                onChange={handleChange}
              >
                {availablePositionOptions.map((position) => (
                  <MenuItem key={position.id} value={position.id}>{t(position.label)}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('cancel')}</Button>
        <LoadingButton loading={loading} onClick={handleSave} variant="contained" color="primary">
          {t('save')}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default EditUserModal;
